import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "./data.service";
import {
  StudyReportLogisticsInfo,
  StudyReportBatchItemInfo,
  StudyReportBatchEditInfo,
  StudyReportDeliverySearchItem,
  StudyReportDeliveryEditInfo,
  StudyReportBatch,
} from "../model/logistics/study-report";
import {
  OrderLogisticsInfo,
  OrderBatch,
  OrderBatchEditInfo,
  OrderBatchItem,
} from "../model/logistics/order";
import { StudyReportDeliveryObservation } from "../model/logistics/study-report/study-report-delivery-observation";
import { StudyReportSearchItem } from "../model/study-report-search-item";
import { SectorFilter } from "../model/sector-filter";

@Injectable()
export class LogisticsService {
  constructor(private http: HttpClient, private dataService: DataService) {}

  private readonly getStudyReportBatchesToReceiveEndPoint: string =
    "/logistics/getStudyReportBatchesToReceive/";
  private readonly getOrderBatchesToReceiveEndPoint: string =
    "/logistics/getOrderBatchesToReceive/";
  private readonly studyReportBatchSendEndPoint: string =
    "/logistics/studyReportBatchSend/";
  private readonly studyReportBatchUpdateEndPoint: string =
    "/logistics/studyReportBatchUpdate/";
  private readonly studyReportBatchItemAddProtocoloSendEndPoint: string =
    "/logistics/studyReportBatchItemAddProtocoloSend/";
  private readonly getStudyReportBatchesEndPoint: string =
    "/logistics/getStudyReportBatches/";
  private readonly studyReportBatchDeleteEndPoint: string =
    "/logistics/studyReportBatchDelete/";
  private readonly studyReportBatchEnableEndPoint: string =
    "/logistics/studyReportBatchEnable/";
  private readonly studyReportBatchReceiveEndPoint: string =
    "/logistics/studyReportBatchReceive/";
  private readonly studyReportBatchRollBackEndPoint: string =
    "/logistics/studyReportBatchRollBack/";
  private readonly orderBatchDeleteEndPoint: string =
    "/logistics/orderBatchDelete/";
  private readonly orderBatchEnableEndPoint: string =
    "/logistics/orderBatchEnable/";
  private readonly orderBatchUpdateEndPoint: string =
    "/logistics/orderBatchUpdate/";
  private readonly orderBatchSendEndPoint: string =
    "/logistics/orderBatchSend/";
  private readonly orderBatchReceiveEndPoint: string =
    "/logistics/orderBatchReceive/";
  private readonly orderBatchRollBackEndPoint: string =
    "/logistics/orderBatchRollBack/";
  private readonly orderBatchItemAddProtocoloSendEndPoint: string =
    "/logistics/orderBatchItemAddProtocoloSend/";
  private readonly studyReportDeliveryRollBackEndPoint: string =
    "/logistics/studyReportDeliveryRollBack/";
  private readonly getStudyReportDeliveryObservationEndPoint: string =
    "/logistics/getStudyReportDeliveryObservation/";
  private readonly getStudyReportDeliveryEditInfoEndPoint: string =
    "/logistics/getStudyReportDeliveryEditInfo/";
  private readonly studyReportDeliveryEndPoint: string =
    "/logistics/studyReportDelivery/";
  private readonly getOrderBatchesEndPoint: string =
    "/logistics/getOrderBatches/";
  private readonly getOrderBatchEditInfoEndPoint: string =
    "/logistics/getOrderBatchEditInfo/";
  private readonly orderBatchItemAddProtocoloIndividualSendEndPoint: string =
    "/logistics/orderBatchItemAddProtocoloIndividualSend/";
  private readonly orderBatchIndividualSendEndPoint: string =
    "/logistics/orderBatchIndividualSend/";
  private readonly getStudyReportBatchEditInfoEndPoint: string =
    "/logistics/getStudyReportBatchEditInfo/";
  private readonly studyReportBatchItemAddProtocoloIndividualSendEndPoint: string =
    "/logistics/studyReportBatchItemAddProtocoloIndividualSend/";
  private readonly studyReportBatchIndividualSendEndPoint: string =
    "/logistics/studyReportBatchIndividualSend/";
  private readonly getStudyReportDeliverySearchItemsdEndPoint: string =
    "/logistics/getStudyReportDeliverySearchItems/";
  private readonly getMedicalOrderLogisticInfoEndPoint: string =
    "/logistics/getMedicalOrderLogisticInfo/";
  private readonly getStudyReportLogisticsInfoEndPoint: string =
    "/logistics/getStudyReportLogisticsInfo/";
  private readonly studyReportDeliveryForceReceiveEndPoint: string =
    "/logistics/studyReportDeliveryForceReceive/";
  private readonly studyReportBatchIndividualSendFromStudyControlEndPoint: string =
    "/logistics/StudyReportBatchIndividualSendFromStudyControl/";

  async studyReportBatchIndividualSendFromStudyControl(
    sectorSource: SectorFilter,
    sectorDestination: SectorFilter,
    studyReportSearchItems: StudyReportSearchItem[]
  ): Promise<boolean> {
    const request = {
      SectorSource: sectorSource,
      SectorDestination: sectorDestination,
      StudyReportSearchItems: studyReportSearchItems,
    };
    return await this.dataService
      .post<boolean>(
        this.studyReportBatchIndividualSendFromStudyControlEndPoint,
        request
      )
      .toPromise();
  }

  async getStudyReportLogisticsInfo(
    protocol: string
  ): Promise<StudyReportLogisticsInfo[]> {
    const params = { protocol: protocol };
    return await this.dataService
      .get<StudyReportLogisticsInfo[]>(
        this.getStudyReportLogisticsInfoEndPoint,
        params
      )
      .toPromise();
  }

  async getMedicalOrderLogisticInfo(
    protocol: string
  ): Promise<OrderLogisticsInfo[]> {
    const params = { protocol: protocol };
    return await this.dataService
      .get<OrderLogisticsInfo[]>(
        this.getMedicalOrderLogisticInfoEndPoint,
        params
      )
      .toPromise();
  }

  async getOrderBatchesToReceive(
    receive: boolean,
    name: string,
    sectorSendId?: number,
    sectorReceiverId?: number
  ): Promise<OrderBatch[]> {
    const params = {
      receive: receive,
      name: name,
      sectorSendId: sectorSendId,
      sectorReceiverId: sectorReceiverId,
    };

    return await this.dataService
      .get<OrderBatch[]>(this.getOrderBatchesToReceiveEndPoint, params)
      .toPromise();
  }

  async getStudyReportBatchesToReceive(
    receive: boolean,
    name: string,
    sectorSendId?: number,
    sectorReceiverId?: number
  ): Promise<StudyReportBatch[]> {
    const params = {
      receive: receive,
      name: name,
      sectorSendId: sectorSendId,
      sectorReceiverId: sectorReceiverId,
    };

    return await this.dataService
      .get<StudyReportBatch[]>(
        this.getStudyReportBatchesToReceiveEndPoint,
        params
      )
      .toPromise();
  }

  async studyReportBatchItemAddProtocoloSend(
    studyReportBatchEditInfo: StudyReportBatchEditInfo
  ): Promise<StudyReportBatchItemInfo[]> {
    return await this.dataService
      .post<StudyReportBatchItemInfo[]>(
        this.studyReportBatchItemAddProtocoloSendEndPoint,
        studyReportBatchEditInfo
      )
      .toPromise();
  }

  async studyReportBatchUpdate(
    studyReportBatchEditInfo: StudyReportBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.studyReportBatchUpdateEndPoint,
        studyReportBatchEditInfo
      )
      .toPromise();
  }

  async studyReportBatchRollBack(
    studyReportBatch: StudyReportBatch,
    sectorId: number
  ): Promise<boolean> {
    const request = {
      StudyReportBatch: studyReportBatch,
      SectorId: sectorId,
    };
    return await this.dataService
      .post<boolean>(this.studyReportBatchRollBackEndPoint, request)
      .toPromise();
  }

  async studyReportBatchReceive(
    studyReportBatchEditInfo: StudyReportBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.studyReportBatchReceiveEndPoint,
        studyReportBatchEditInfo
      )
      .toPromise();
  }

  async studyReportBatchSend(
    studyReportBatchEditInfo: StudyReportBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.studyReportBatchSendEndPoint,
        studyReportBatchEditInfo
      )
      .toPromise();
  }

  async studyReportBatchDelete(
    studyReportBatch: StudyReportBatch
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.studyReportBatchDeleteEndPoint, studyReportBatch)
      .toPromise();
  }

  async studyReportBatchEnable(
    studyReportBatch: StudyReportBatch
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.studyReportBatchEnableEndPoint, studyReportBatch)
      .toPromise();
  }

  async getStudyReportBatches(
    showDisabled: boolean,
    send: boolean,
    name: string,
    sectorSendId?: number,
    sectorReceiverId?: number
  ): Promise<StudyReportBatch[]> {
    const params = {
      showDisabled: showDisabled,
      send: send,
      name: name,
      sectorSendId: sectorSendId,
      sectorReceiverId: sectorReceiverId,
    };

    return await this.dataService
      .get<StudyReportBatch[]>(this.getStudyReportBatchesEndPoint, params)
      .toPromise();
  }

  async getStudyReportDeliveryObservation(
    studyReportId: number
  ): Promise<StudyReportDeliveryObservation> {
    const params = { studyReportId: studyReportId };
    return await this.dataService
      .get<StudyReportDeliveryObservation>(
        this.getStudyReportDeliveryObservationEndPoint,
        params
      )
      .toPromise();
  }

  async getStudyReportDeliverySearchItems(
    showAll: boolean,
    protocol: string,
    patientId?: number
  ): Promise<StudyReportDeliverySearchItem> {
    const params = {
      showAll: showAll,
      protocol: protocol,
      patientId: patientId,
    };
    return await this.dataService
      .get<StudyReportDeliverySearchItem>(
        this.getStudyReportDeliverySearchItemsdEndPoint,
        params
      )
      .toPromise();
  }

  async getStudyReportDeliveryEditInfo(
    reportStudyId: number
  ): Promise<StudyReportDeliveryEditInfo> {
    const params = { studyReportId: reportStudyId };
    return await this.dataService
      .get<StudyReportDeliveryEditInfo>(
        this.getStudyReportDeliveryEditInfoEndPoint,
        params
      )
      .toPromise();
  }

  async getStudyReportBatchEditInfo(
    loguinSectorId: number,
    studyReportBatchId?: number
  ): Promise<StudyReportBatchEditInfo> {
    const params = {
      loguinSectorId: loguinSectorId,
      studyReportBatchId: studyReportBatchId,
    };

    return await this.dataService
      .get<StudyReportBatchEditInfo>(
        this.getStudyReportBatchEditInfoEndPoint,
        params
      )
      .toPromise();
  }

  async studyReportBatchItemAddProtocoloIndividualSend(
    studyReportBatchEditInfo: StudyReportBatchEditInfo
  ): Promise<StudyReportBatchItemInfo[]> {
    return await this.dataService
      .post<StudyReportBatchItemInfo[]>(
        this.studyReportBatchItemAddProtocoloIndividualSendEndPoint,
        studyReportBatchEditInfo
      )
      .toPromise();
  }

  async studyReportBatchIndividualSend(
    studyReportBatchEditInfo: StudyReportBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(
        this.studyReportBatchIndividualSendEndPoint,
        studyReportBatchEditInfo
      )
      .toPromise();
  }

  async getOrderBatches(
    showDisabled: boolean,
    send: boolean,
    name: string,
    sectorSendId?: number,
    sectorReceiverId?: number
  ): Promise<OrderBatch[]> {
    const params = {
      showDisabled: showDisabled,
      send: send,
      name: name,
      sectorSendId: sectorSendId,
      sectorReceiverId: sectorReceiverId,
    };

    return await this.dataService
      .get<OrderBatch[]>(this.getOrderBatchesEndPoint, params)
      .toPromise();
  }

  async getOrderBatchEditInfo(
    loguinSectorId: number,
    orderBatchId?: number
  ): Promise<OrderBatchEditInfo> {
    const params = {
      loguinSectorId: loguinSectorId,
      orderBatchId: orderBatchId,
    };

    return await this.dataService
      .get<OrderBatchEditInfo>(this.getOrderBatchEditInfoEndPoint, params)
      .toPromise();
  }

  async orderBatchDelete(orderBatch: OrderBatch): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.orderBatchDeleteEndPoint, orderBatch)
      .toPromise();
  }

  async orderBatchEnable(orderBatch: OrderBatch): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.orderBatchEnableEndPoint, orderBatch)
      .toPromise();
  }

  async orderBatchItemAddProtocoloSend(
    orderBatchEditInfo: OrderBatchEditInfo
  ): Promise<OrderBatchItem[]> {
    return await this.dataService
      .post<OrderBatchItem[]>(
        this.orderBatchItemAddProtocoloSendEndPoint,
        orderBatchEditInfo
      )
      .toPromise();
  }

  async orderBatchItemAddProtocoloIndividualSend(
    orderBatchEditInfo: OrderBatchEditInfo
  ): Promise<OrderBatchItem[]> {
    return await this.dataService
      .post<OrderBatchItem[]>(
        this.orderBatchItemAddProtocoloIndividualSendEndPoint,
        orderBatchEditInfo
      )
      .toPromise();
  }

  async orderBatchUpdate(
    orderBatchEditInfo: OrderBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.orderBatchUpdateEndPoint, orderBatchEditInfo)
      .toPromise();
  }

  async orderBatchSend(
    orderBatchEditInfo: OrderBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.orderBatchSendEndPoint, orderBatchEditInfo)
      .toPromise();
  }

  async orderBatchRollBack(
    orderBatch: OrderBatch,
    sectorId: number
  ): Promise<boolean> {
    const request = {
      OrderBatch: orderBatch,
      SectorId: sectorId,
    };
    return await this.dataService
      .post<boolean>(this.orderBatchRollBackEndPoint, request)
      .toPromise();
  }

  async orderBatchReceive(
    orderBatchEditInfo: OrderBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.orderBatchReceiveEndPoint, orderBatchEditInfo)
      .toPromise();
  }

  async orderBatchIndividualSend(
    orderBatchEditInfo: OrderBatchEditInfo
  ): Promise<boolean> {
    return await this.dataService
      .post<boolean>(this.orderBatchIndividualSendEndPoint, orderBatchEditInfo)
      .toPromise();
  }

  async studyReportDelivery(
    studyReportId: number,
    deliveryObservation: string
  ): Promise<boolean> {
    const params = {
      StudyReportId: studyReportId,
      DeliveryObservation: deliveryObservation,
    };
    return await this.dataService
      .post<boolean>(this.studyReportDeliveryEndPoint, params)
      .toPromise();
  }

  async studyReportDeliveryRollBack(studyReportId: number): Promise<boolean> {
    const params = { StudyReportId: studyReportId };
    return await this.dataService
      .post<boolean>(this.studyReportDeliveryRollBackEndPoint, params)
      .toPromise();
  }

  async studyReportDeliveryForceReceive(
    studyReportId: number,
    sectorId: number
  ): Promise<boolean> {
    const request = {
      StudyReportId: studyReportId,
      SectorId: sectorId,
    };

    return await this.dataService
      .post<boolean>(this.studyReportDeliveryForceReceiveEndPoint, request)
      .toPromise();
  }
}
